import React from 'react';
import { createPortal } from 'react-dom';
import infoIcon from '../../assets/tooltipInfoIcon.svg';

type InfoTooltipProps = {
  text: React.ReactNode;
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  iconColor?: string;
  /** Which side the tooltip bubble opens toward. Default: 'right' */
  placement?: 'right' | 'top' | 'left';
};

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  text,
  ariaLabel = 'Mostrar información',
  className,
  style,
  disabled = false,
  iconColor,
  placement = 'right',
}) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const tooltipIdRef = React.useRef<string>(`tip-${Math.random().toString(36).slice(2, 9)}`);
  const tooltipId = tooltipIdRef.current;

  // Posición fija calculada desde el ícono — se renderiza vía portal en <body> para no
  // quedar recortado por ancestros con overflow (ej: el scroll horizontal de una tabla).
  const [fixedPos, setFixedPos] = React.useState<{ top: number; left: number; transform: string; width?: number } | null>(null);

  const computePosition = React.useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const gap = 8;

    if (window.innerWidth < 640) {
      const tooltipW = Math.min(window.innerWidth - 24, 300);
      let left = r.left + r.width / 2 - tooltipW / 2;
      left = Math.max(12, Math.min(left, window.innerWidth - tooltipW - 12));
      setFixedPos({ top: r.top - gap, left, transform: 'translateY(-100%)', width: tooltipW });
      return;
    }

    if (placement === 'top') {
      setFixedPos({ top: r.top - gap, left: r.left + r.width / 2, transform: 'translate(-50%, -100%)' });
    } else if (placement === 'left') {
      setFixedPos({ top: r.top + r.height / 2, left: r.left - gap, transform: 'translate(-100%, -50%)' });
    } else {
      setFixedPos({ top: r.top + r.height / 2, left: r.right + gap, transform: 'translateY(-50%)' });
    }
  }, [placement]);

  const openTooltip = React.useCallback(() => {
    if (disabled) return;
    computePosition();
    setOpen(true);
  }, [disabled, computePosition]);

  React.useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        ...style,
      }}
      onMouseEnter={() => { if (!disabled && !isMobile) openTooltip(); }}
      onMouseLeave={() => { if (!disabled && !isMobile) setOpen(false); }}
    >
      <style>{`
        .itp-icon-btn { display:inline-flex; align-items:center; justify-content:center; width: 22px; height: 22px; border: 0; background: transparent; cursor: pointer; border-radius: 9999px; }
        .itp-icon-btn:hover { background: rgba(0,0,0,0.06); }
        .itp-tooltip {
          position: fixed;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 0.5rem 0.75rem;
          color: #6b7280;
          font-size: 0.9rem;
          line-height: 1.35;
          width: max-content;
          min-width: 280px;
          max-width: 420px;
          white-space: normal;
          word-break: break-word;
          box-shadow: 0 10px 25px rgba(0,0,0,0.08);
          z-index: 9999;
          text-align: left;
        }
        .itp-tooltip.itp-right:after {
          content: "";
          position: absolute;
          right: 100%;
          top: 50%;
          transform: translateY(-50%) rotate(45deg);
          width: 10px;
          height: 10px;
          background: #fff;
          border-left: 1px solid #e5e7eb;
          border-top: 1px solid #e5e7eb;
        }
        .itp-tooltip.itp-left:after {
          content: "";
          position: absolute;
          left: 100%;
          top: 50%;
          transform: translateY(-50%) rotate(45deg);
          width: 10px;
          height: 10px;
          background: #fff;
          border-right: 1px solid #e5e7eb;
          border-bottom: 1px solid #e5e7eb;
        }
        .itp-tooltip.itp-top:after {
          content: "";
          position: absolute;
          left: 50%;
          top: 100%;
          transform: translateX(-50%) rotate(45deg);
          width: 10px;
          height: 10px;
          background: #fff;
          border-right: 1px solid #e5e7eb;
          border-bottom: 1px solid #e5e7eb;
        }
        .itp-tooltip.itp-mobile {
          min-width: unset;
          max-width: calc(100vw - 24px);
        }
        .itp-tooltip.itp-mobile:after {
          content: "";
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%) rotate(45deg);
          width: 10px;
          height: 10px;
          background: #fff;
          border-right: 1px solid #e5e7eb;
          border-bottom: 1px solid #e5e7eb;
        }
      `}</style>
      <button
        ref={buttonRef}
        type="button"
        className="itp-icon-btn"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={open ? tooltipId : undefined}
        onClick={() => {
          if (disabled) return;
          if (window.innerWidth < 640) {
            openTooltip();
          } else {
            setOpen((v) => {
              const next = !v;
              if (next) computePosition();
              return next;
            });
          }
        }}
        disabled={disabled}
      >
        {iconColor ? (
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="12" cy="12" r="10" stroke={iconColor} strokeWidth="2" />
            <path d="M12 16v-4M12 8h.01" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <img src={infoIcon} alt="" width={16} height={16} aria-hidden />
        )}
      </button>
      {open && fixedPos && createPortal(
        <div
          id={tooltipId}
          role="tooltip"
          className={`itp-tooltip ${window.innerWidth < 640 ? 'itp-mobile' : `itp-${placement}`}`}
          style={{
            top: fixedPos.top,
            left: fixedPos.left,
            transform: fixedPos.transform,
            ...(fixedPos.width ? { width: fixedPos.width } : {}),
          }}
        >
          {text}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default InfoTooltip;
